import React, { useState } from 'react';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { CarrierOptionType } from '../../../../graphql/queries/bagQueries';
import { AutoComplete } from '../../../pda/components/AutoComplete';

interface CarrierSelectorProps {
    /** 尾程商选项列表 */
    options: CarrierOptionType[];
    /** 已选尾程商 value 列表，不选表示不限 */
    selectedValues: number[];
    onChange: (values: number[]) => void;
    isLocked: boolean;
    onLockToggle: () => void;
    /** 占位符，如「尾程商 (不选表示不限)」 */
    label: string;
}

/**
 * 装包扫描页尾程商多选：文本框 + 模糊查询，与下一站同交互；选中的尾程商用于筛选参与段码匹配的包牌。
 */
export const CarrierSelector: React.FC<CarrierSelectorProps> = ({
    options,
    selectedValues,
    onChange,
    isLocked,
    onLockToggle,
    label,
}) => {
    const [inputValue, setInputValue] = useState('');

    const valueToName = React.useMemo(() => {
        const m: Record<number, string> = {};
        options.forEach(o => { m[o.value] = o.name; });
        return m;
    }, [options]);

    const availableOptions = options.filter(o => !selectedValues.includes(o.value));

    const autoCompleteOptions = React.useMemo(() =>
        availableOptions.map(o => ({ label: o.name, value: o.name })),
        [availableOptions]
    );

    const handleSelect = (name: string) => {
        const opt = options.find(o => o.name === name);
        if (!opt || selectedValues.includes(opt.value)) return;
        onChange([...selectedValues, opt.value]);
        setInputValue('');
    };

    const handleRemove = (value: number) => {
        if (isLocked) return;
        onChange(selectedValues.filter(x => x !== value));
    };

    return (
        <div className="w-full">
            <div className="flex items-center gap-2 mb-2">
                {!isLocked && (
                    <div className="flex-1 min-w-0">
                        <AutoComplete
                            id="bagging-scan-carrier-input"
                            name="bagging-scan-carrier"
                            showAllOnFocus
                            className="w-full p-2 border rounded"
                            placeholder={label}
                            value={inputValue}
                            onChange={setInputValue}
                            onSelect={handleSelect}
                            options={autoCompleteOptions}
                            disabled={isLocked}
                            selectedValues={selectedValues.map(v => valueToName[v]).filter(Boolean) as string[]}
                        />
                    </div>
                )}
                <button
                    type="button"
                    onClick={onLockToggle}
                    className="flex items-center justify-center w-10 h-10 shrink-0 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100"
                >
                    {isLocked ? (
                        <LockOutlined className="text-blue-500 text-lg" />
                    ) : (
                        <UnlockOutlined className="text-gray-400 text-lg" />
                    )}
                </button>
            </div>
            {selectedValues.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {selectedValues.map((value) => (
                        <div
                            key={value}
                            className={`px-3 py-1 rounded-md text-sm flex items-center gap-1 ${
                                isLocked
                                    ? 'bg-gray-200 text-gray-600'
                                    : 'bg-blue-100 text-blue-800'
                            }`}
                        >
                            <span>{valueToName[value] ?? value}</span>
                            {!isLocked && (
                                <button
                                    type="button"
                                    onClick={() => handleRemove(value)}
                                    className="text-blue-600 hover:text-blue-800 font-bold ml-1 text-base leading-none"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
